<script setup lang="ts">
import WrButton from '~/components/global/WrButton.vue';
import WrIcon from '~/components/global/WrIcon.vue';

const colorMode = useColorMode();

interface StepCard {
  title: string;
  description: string;
  icon?: string;
  svgIcon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: StepCard[];
    ctaText?: string;
    ctaLink?: string | null;
  }>(),
  {
    ctaText: 'Schedule a Demo',
    ctaLink: null,
  }
);

const emit = defineEmits<{
  'cta-clicked': [];
}>();
</script>

<template>
  <div class="mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]">
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
      style="font-weight: 600; background: linear-gradient(92deg, #12DBFF -3.57%, #BA55FE 80.72%); background-clip: text; -webkit-background-clip: text; -webkit-text-fill-color: transparent;"
    >
      <span v-html="heading"></span>
    </h2>
    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 px-4 lg:opacity-70 text-center font-avant"
      :class="colorMode.preference === 'dark' ? 'text-[#D9D9D9]' : 'text-[#000000]'"
    >
      {{ subcopy }}
    </p>

    <!-- Step Cards -->
    <div class="flex flex-col sm:flex-row items-stretch lg:justify-center gap-6 mt-10 px-4 flex-wrap">
      <div
        v-for="(card, i) in cards"
        :key="i"
        class="audience-box relative flex flex-col flex-1 p-4 text-left"
        :class="colorMode.preference === 'dark' ? 'audience-box-dark' : 'audience-box-light'"
      >
        <div class="size-8 sm:size-10 flex-shrink-0" :class="colorMode.preference === 'dark' ? 'audience-icon-dark' : 'audience-icon-light'">
          <WrIcon
            v-if="card.svgIcon"
            :name="card.svgIcon"
            class="size-8 max-sm:size-6 text-black dark:text-white"
          />
          <WrIcon
            v-else-if="card?.icon"
            :name="card.icon"
            class="size-8 max-sm:size-6 text-black dark:text-white"
          />
          <slot :name="`icon-${i}`" :index="i" v-else>
            <!-- Default placeholder icon -->
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
              <rect width="40" height="40" rx="8" fill="currentColor" fill-opacity="0.1" />
              <text x="20" y="24" text-anchor="middle" fill="currentColor" font-size="16">{{ i + 1 }}</text>
            </svg>
          </slot>
        </div>
        <h3 class="mt-3 mb-2 self-stretch font-avant" :class="colorMode.preference === 'dark' ? 'text-white' : 'text-black'" style="font-size: 20px; font-weight: 400; line-height: normal;">
          {{ card.title }}
        </h3>
        <p class="mt-auto font-avant" :class="colorMode.preference === 'dark' ? 'text-[#AF66E4]' : 'text-[#501D71]'" style="font-size: 15px; font-weight: 400; line-height: normal; letter-spacing: 0.3px;">
          {{ card.description }}
        </p>
      </div>
    </div>

    <!-- CTA Button -->
    <div class="flex justify-center mt-6 lg:mt-12">
      <WrButton :link="ctaLink || undefined" @click="!ctaLink ? emit('cta-clicked') : undefined" container-class="h-[40px] lg:h-[48px]" append-class="text-md lg:font-normal px-3 lg:px-5">
        {{ ctaText }}
      </WrButton>
    </div>
  </div>
</template>

<style scoped>
.audience-box {
  border-radius: 9px;
}

@media (max-width: 1023px) {
  .audience-box {
    width: 100%;
    min-width: unset;
    min-height: 160px;
  }
}

@media (min-width: 1024px) {
  .audience-box {
    min-width: 240px;
  }
}

.audience-box-dark {
  border: 1px solid #3D1B60;
}

.audience-box-light {
  border: 1px solid #A74FFF;
}

.audience-icon-dark svg path {
  fill: #FFF;
}

.audience-icon-light svg path {
  fill: #000;
}
</style>
